import React, { useState, useEffect } from 'react';
import { Table, Spin, Card, message, Button, Modal, Space, Input, Form, Tabs, Tag, Popconfirm, Badge, Tooltip, Progress, Row, Col, Statistic, List, Empty } from 'antd';
import { ReloadOutlined, StopOutlined, UnlockOutlined, SearchOutlined, PlusOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { adminAPI } from '../api/admin';

// نمودار روند نصب‌های روزانه با فرمت SVG بدون وابستگی خارجی برای لود سریع
function InstallationTrendChart({ data }) {
  const width = 800;
  const height = 180;
  const padding = 20;

  if (!data || data.length === 0) {
    return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb' }}>داده‌ای موجود نیست</div>;
  }

  const values = data.map(d => Number(d.count) || 0);
  const max = Math.max(...values, 1);
  const step = (width - padding * 2) / Math.max(values.length - 1, 1);

  const points = values.map((v, i) => {
    const x = padding + i * step;
    const y = height - padding - (v / max) * (height - padding * 2);
    return [x, y];
  });

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${points[points.length - 1][0].toFixed(1)},${height - padding} L${points[0][0].toFixed(1)},${height - padding} Z`;

  return (
    <Card title="روند نصب‌های روزانه (۱۴ روز گذشته)" size="small">
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height: 180, display: 'block' }}>
        <defs>
          <linearGradient id="chartGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#722f37" stopOpacity="0.2"/>
            <stop offset="100%" stopColor="#722f37" stopOpacity="0"/>
          </linearGradient>
        </defs>
        {/* خطوط پس‌زمینه */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, idx) => {
          const y = padding + ratio * (height - padding * 2);
          return (
            <line key={idx} x1={padding} y1={y} x2={width - padding} y2={y} stroke="#f5f5f5" strokeDasharray="3" />
          );
        })}
        <path d={areaPath} fill="url(#chartGrad)" />
        <path d={linePath} fill="none" stroke="#722f37" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => (
          <Tooltip title={`تعداد: ${values[i]} نصب`} key={i}>
            <circle cx={p[0]} cy={p[1]} r={4} fill="#722f37" style={{ cursor: 'pointer' }} />
          </Tooltip>
        ))}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, color: '#888', fontSize: 11, direction: 'ltr' }}>
        <span>{data[0]?.date || ''}</span>
        <span>{data[data.length - 1]?.date || ''}</span>
      </div>
    </Card>
  );
}

export default function DevicesBlacklistManager() {
  const [activeTab, setActiveTab] = useState('devices');
  const [loading, setLoading] = useState(false);
  
  // Tab 1: Devices State
  const [devices, setDevices] = useState([]);
  const [devicesPagination, setDevicesPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [deviceSearch, setDeviceSearch] = useState('');
  const [banDeviceModal, setBanDeviceModal] = useState(null); // device record
  const [banDeviceReason, setBanDeviceReason] = useState('');
  const [banDeviceSubmitting, setBanDeviceSubmitting] = useState(false);

  // Tab 2: Blacklist State
  const [blacklist, setBlacklist] = useState([]);
  const [blacklistPagination, setBlacklistPagination] = useState({ page: 1, limit: 10, total: 0 });
  const [blacklistTypeFilter, setBlacklistTypeFilter] = useState(null);
  const [addPhoneModalVisible, setAddPhoneModalVisible] = useState(false);
  const [addPhoneForm] = Form.useForm();
  const [addPhoneSubmitting, setAddPhoneSubmitting] = useState(false);

  // Tab 3: Stats State
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (activeTab === 'devices') {
      loadDevices();
    } else if (activeTab === 'blacklist') {
      loadBlacklist();
    } else if (activeTab === 'stats') {
      loadStats();
    }
  }, [activeTab]);

  // ─── Tab 1: Devices Actions ───────────────────────────────────────────────
  
  const loadDevices = async (page = 1, search = deviceSearch) => {
    setLoading(true);
    try {
      const filters = {};
      if (search.trim()) filters.search = search.trim();
      
      const response = await adminAPI.getDevices(page, 10, filters);
      if (response.data.success) {
        setDevices(response.data.devices);
        setDevicesPagination({
          page,
          limit: 10,
          total: response.data.pagination.total
        });
      }
    } catch (error) {
      message.error('خطا در بارگذاری لیست دستگاه‌ها');
    } finally {
      setLoading(false);
    }
  };

  const handleDeviceSearch = () => {
    loadDevices(1, deviceSearch);
  };

  const openBanDeviceModal = (record) => {
    setBanDeviceModal(record);
    setBanDeviceReason('');
  };

  const submitBanDevice = async () => {
    if (!banDeviceModal) return;
    setBanDeviceSubmitting(true);
    try {
      const res = await adminAPI.banDevice(banDeviceModal.deviceId, banDeviceReason.trim());
      if (res.data.success) {
        message.success('دستگاه با موفقیت مسدود شد');
        setBanDeviceModal(null);
        loadDevices(devicesPagination.page);
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در مسدودسازی دستگاه');
    } finally {
      setBanDeviceSubmitting(false);
    }
  };

  const handleUnbanDevice = async (record) => {
    try {
      const res = await adminAPI.unbanDevice(record.deviceId);
      if (res.data.success) {
        message.success('مسدودیت دستگاه با موفقیت لغو شد');
        loadDevices(devicesPagination.page);
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در رفع مسدودیت دستگاه');
    }
  };

  // ─── Tab 2: Blacklist Actions ─────────────────────────────────────────────
  
  const loadBlacklist = async (page = 1, type = blacklistTypeFilter) => {
    setLoading(true);
    try {
      const response = await adminAPI.getBlacklist(page, 10, type);
      if (response.data.success) {
        setBlacklist(response.data.blacklist);
        setBlacklistPagination({
          page,
          limit: 10,
          total: response.data.pagination.total
        });
      }
    } catch (error) {
      message.error('خطا در بارگذاری لیست سیاه');
    } finally {
      setLoading(false);
    }
  };

  const submitBanPhone = async (values) => {
    setAddPhoneSubmitting(true);
    try {
      const res = await adminAPI.banPhoneNumber(values.phoneNumber, values.reason?.trim());
      if (res.data.success) {
        message.success('شماره تماس با موفقیت به لیست سیاه اضافه شد');
        setAddPhoneModalVisible(false);
        addPhoneForm.resetFields();
        loadBlacklist(1);
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در مسدودسازی شماره تماس');
    } finally {
      setAddPhoneSubmitting(false);
    }
  };

  const handleRemoveFromBlacklist = async (record) => {
    try {
      let res;
      if (record.type === 'phone') {
        res = await adminAPI.unbanPhoneNumber(record.value);
      } else {
        res = await adminAPI.unbanDevice(record.value);
      }
      
      if (res.data.success) {
        message.success('آیتم با موفقیت از لیست سیاه حذف شد');
        loadBlacklist(blacklistPagination.page);
      }
    } catch (error) {
      message.error(error?.response?.data?.message || 'خطا در حذف از لیست سیاه');
    }
  };

  // ─── Tab 3: Stats Actions ─────────────────────────────────────────────────
  
  const loadStats = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getDeviceStats();
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      message.error('خطا در دریافت آمار دستگاه‌ها');
    } finally {
      setLoading(false);
    }
  };

  const renderBreakdown = (title, items, total) => {
    if (!items || items.length === 0) {
      return (
        <Card title={title} bordered={false} size="small" style={{ height: '100%' }}>
          <Empty description="داده‌ای یافت نشد" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        </Card>
      );
    }
    return (
      <Card title={title} bordered={false} size="small" style={{ height: '100%' }}>
        <List
          dataSource={items}
          renderItem={(item) => {
            const percent = total > 0 ? (item.count / total) * 100 : 0;
            return (
              <List.Item style={{ display: 'block', border: 'none', padding: '8px 0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
                  <span>{item._id || 'نامشخص'}</span>
                  <span><strong>{item.count} نصب</strong> ({percent.toFixed(1)}٪)</span>
                </div>
                <Progress percent={Math.round(percent)} strokeColor="#722f37" showInfo={false} size="small" />
              </List.Item>
            );
          }}
        />
      </Card>
    );
  };

  // ─── Columns Definitions ──────────────────────────────────────────────────
  
  const deviceColumns = [
    {
      title: 'شناسه دستگاه (Device ID)',
      dataIndex: 'deviceId',
      render: (val) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{val}</span>
    },
    {
      title: 'کاربر مرتبط',
      render: (_, record) => {
        if (!record.userId) {
          return record.phoneNumber ? (
            <Space direction="vertical" size={2}>
              <Tag color="orange">مهمان (فقط شماره)</Tag>
              <span style={{ fontSize: 12 }}>{record.phoneNumber}</span>
            </Space>
          ) : (
            <Tag>کاربر ناشناس / مهمان</Tag>
          );
        }
        const u = record.userId;
        return (
          <Space direction="vertical" size={2}>
            <strong>{u.firstName} {u.lastName}</strong>
            <span style={{ fontSize: 12, color: '#888' }}>
              {u.username} | {u.phoneNumber}
            </span>
          </Space>
        );
      }
    },
    {
      title: 'مشخصات فنی دستگاه',
      render: (_, record) => (
        <Space direction="vertical" size={2}>
          <span>{record.brand || '-'} {record.model || '-'}</span>
          <span style={{ fontSize: 11, color: '#888' }}>{record.osVersion || '-'}</span>
        </Space>
      )
    },
    {
      title: 'نسخه اپلیکیشن',
      dataIndex: 'appVersion',
      render: (val) => val ? <Tag color="blue">v{val}</Tag> : '-'
    },
    {
      title: 'آدرس IP',
      dataIndex: 'ipAddress',
      render: (val) => <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{val || '-'}</span>
    },
    {
      title: 'آخرین فعالیت',
      dataIndex: 'lastActiveAt',
      render: (val) => val ? new Date(val).toLocaleString('fa-IR') : '-'
    },
    {
      title: 'وضعیت دسترسی',
      render: (_, record) => (
        record.banned ? (
          <Badge status="error" text={<span style={{ color: 'red', fontWeight: 'bold' }}>مسدود شده</span>} />
        ) : (
          <Badge status="success" text="فعال و مجاز" />
        )
      )
    },
    {
      title: 'عملیات',
      render: (_, record) => (
        record.banned ? (
          <Popconfirm
            title="رفع مسدودیت دسترسی این دستگاه؟"
            okText="بله، باز کن"
            cancelText="انصراف"
            onConfirm={() => handleUnbanDevice(record)}
          >
            <Button size="small" icon={<UnlockOutlined />}>رفع مسدودیت</Button>
          </Popconfirm>
        ) : (
          <Button size="small" danger icon={<StopOutlined />} onClick={() => openBanDeviceModal(record)}>
            مسدود کردن
          </Button>
        )
      )
    }
  ];

  const blacklistColumns = [
    {
      title: 'نوع مسدودیت',
      dataIndex: 'type',
      render: (type) => type === 'phone' ? <Tag color="magenta">شماره تلفن</Tag> : <Tag color="volcano">شناسه دستگاه</Tag>
    },
    {
      title: 'مقدار مسدود شده',
      dataIndex: 'value',
      render: (val) => <strong style={{ fontFamily: 'monospace' }}>{val}</strong>
    },
    {
      title: 'علت مسدودسازی',
      dataIndex: 'reason',
      render: (reason) => reason ? (
        <span>{reason}</span>
      ) : (
        <span style={{ color: '#bbb', fontStyle: 'italic' }}>علتی ثبت نشده است</span>
      )
    },
    {
      title: 'تاریخ ثبت',
      dataIndex: 'createdAt',
      render: (val) => new Date(val).toLocaleString('fa-IR')
    },
    {
      title: 'عملیات',
      render: (_, record) => (
        <Popconfirm
          title="آیا مطمئن هستید که می‌خواهید مسدودیت این آیتم را لغو کنید؟"
          okText="بله، رفع مسدودیت"
          cancelText="انصراف"
          onConfirm={() => handleRemoveFromBlacklist(record)}
        >
          <Button size="small" icon={<UnlockOutlined />}>رفع مسدودیت</Button>
        </Popconfirm>
      )
    }
  ];

  const tabItems = [
    {
      key: 'devices',
      label: 'دستگاه‌های ثبت شده',
      children: (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', gap: 10 }}>
            <Input
              placeholder="جستجو در شناسه دستگاه، برند، مدل، یا شماره تلفن..."
              value={deviceSearch}
              onChange={(e) => setDeviceSearch(e.target.value)}
              onPressEnter={handleDeviceSearch}
              style={{ width: 350 }}
              prefix={<SearchOutlined style={{ color: '#ccc' }} />}
              allowClear
            />
            <Button type="primary" onClick={handleDeviceSearch}>جستجو</Button>
            <Button icon={<ReloadOutlined />} onClick={() => loadDevices(1)}>تازه‌سازی</Button>
          </div>

          <Table
            columns={deviceColumns}
            dataSource={devices}
            rowKey="_id"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: devicesPagination.page,
              pageSize: devicesPagination.limit,
              total: devicesPagination.total,
              onChange: (page) => loadDevices(page)
            }}
          />
        </div>
      )
    },
    {
      key: 'blacklist',
      label: 'لیست سیاه (Blacklist)',
      children: (
        <div>
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <Button 
                onClick={() => { setBlacklistTypeFilter(null); loadBlacklist(1, null); }} 
                type={blacklistTypeFilter === null ? 'primary' : 'default'}
              >
                همه
              </Button>
              <Button 
                onClick={() => { setBlacklistTypeFilter('phone'); loadBlacklist(1, 'phone'); }} 
                type={blacklistTypeFilter === 'phone' ? 'primary' : 'default'}
              >
                فقط شماره تلفن‌ها
              </Button>
              <Button 
                onClick={() => { setBlacklistTypeFilter('device'); loadBlacklist(1, 'device'); }} 
                type={blacklistTypeFilter === 'device' ? 'primary' : 'default'}
              >
                فقط شناسه‌های دستگاه
              </Button>
              <Button icon={<ReloadOutlined />} onClick={() => loadBlacklist(1)}>تازه‌سازی</Button>
            </Space>
            <Button type="primary" danger icon={<PlusOutlined />} onClick={() => setAddPhoneModalVisible(true)}>
              مسدودسازی شماره تلفن جدید
            </Button>
          </div>

          <Table
            columns={blacklistColumns}
            dataSource={blacklist}
            rowKey="_id"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: blacklistPagination.page,
              pageSize: blacklistPagination.limit,
              total: blacklistPagination.total,
              onChange: (page) => loadBlacklist(page)
            }}
          />
        </div>
      )
    },
    {
      key: 'stats',
      label: 'آمار و آنالیتیکس نصب',
      children: stats ? (
        <div>
          {/* KPI Cards */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ textAlign: 'center', borderTop: '4px solid #722f37' }}>
                <Statistic title="کل دستگاه‌های نصب شده" value={stats.totalDevices} valueStyle={{ color: '#722f37', fontWeight: 'bold' }} />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ textAlign: 'center', borderTop: '4px solid #2db7f5' }}>
                <Statistic title="فعال در ۲۴ ساعت گذشته" value={stats.active24h} valueStyle={{ color: '#2db7f5', fontWeight: 'bold' }} />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ textAlign: 'center', borderTop: '4px solid #87d068' }}>
                <Statistic title="فعال در ۷ روز گذشته" value={stats.active7d} valueStyle={{ color: '#87d068', fontWeight: 'bold' }} />
              </Card>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Card style={{ textAlign: 'center', borderTop: '4px solid #f50' }}>
                <Statistic title="دستگاه‌های مسدود شده" value={stats.bannedDevicesCount} valueStyle={{ color: '#f50', fontWeight: 'bold' }} />
              </Card>
            </Col>
          </Row>

          {/* Trend Chart */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <InstallationTrendChart data={stats.installationTrend} />
            </Col>
          </Row>

          {/* Spec Breakdowns */}
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={24} md={12} lg={6}>
              {renderBreakdown('برندهای محبوب', stats.brandStats, stats.totalDevices)}
            </Col>
            <Col xs={24} sm={24} md={12} lg={6}>
              {renderBreakdown('مدل‌های محبوب', stats.modelStats, stats.totalDevices)}
            </Col>
            <Col xs={24} sm={24} md={12} lg={6}>
              {renderBreakdown('نسخه‌های سیستم‌عامل (OS)', stats.osStats, stats.totalDevices)}
            </Col>
            <Col xs={24} sm={24} md={12} lg={6}>
              {renderBreakdown('نسخه‌های اپلیکیشن', stats.appVersionStats, stats.totalDevices)}
            </Col>
          </Row>
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '50px 0' }}>
          <Spin size="large" tip="در حال بارگذاری آمار و اطلاعات نصب..." />
        </div>
      )
    }
  ];

  return (
    <Card title="مدیریت دستگاه‌ها و لیست سیاه">
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, color: '#666' }}>
        <InfoCircleOutlined style={{ color: '#722f37' }} />
        <span>در این بخش می‌توانید مشخصات دستگاه‌هایی که اپلیکیشن پاترهدها را نصب کرده‌اند مشاهده نموده و در صورت لزوم، دسترسی دستگاه‌ها یا شماره تلفن‌های متخلف را به طور کامل به اپلیکیشن ببندید.</span>
      </div>

      <Spin spinning={loading && activeTab !== 'stats'}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Spin>

      {/* Modal to Ban Device */}
      <Modal
        title={banDeviceModal ? `مسدود کردن دستگاه — ${banDeviceModal.brand || ''} ${banDeviceModal.model || ''}` : ''}
        open={!!banDeviceModal}
        onCancel={() => setBanDeviceModal(null)}
        onOk={submitBanDevice}
        confirmLoading={banDeviceSubmitting}
        okText="مسدود کن"
        okButtonProps={{ danger: true }}
        cancelText="انصراف"
      >
        {banDeviceModal && (
          <Space direction="vertical" style={{ width: '100%' }} size={12}>
            <div>
              با تایید این عملیات، دسترسی این دستگاه با شناسه زیر برای همیشه به اپلیکیشن قطع خواهد شد و در صفحه ورود با خطای مسدودی دستگاه روبرو می‌شود:
            </div>
            <div style={{ padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4, fontFamily: 'monospace', fontSize: 12 }}>
              {banDeviceModal.deviceId}
            </div>
            <div>دلیل مسدودسازی (به کاربر نمایش داده می‌شود):</div>
            <Input.TextArea
              rows={3}
              value={banDeviceReason}
              onChange={(e) => setBanDeviceReason(e.target.value)}
              placeholder="علت مسدودسازی را وارد کنید..."
            />
          </Space>
        )}
      </Modal>

      {/* Modal to Blacklist Phone Number */}
      <Modal
        title="مسدودسازی شماره تلفن جدید"
        open={addPhoneModalVisible}
        onCancel={() => { setAddPhoneModalVisible(false); addPhoneForm.resetFields(); }}
        onOk={() => addPhoneForm.submit()}
        confirmLoading={addPhoneSubmitting}
        okText="افزودن به لیست سیاه"
        okButtonProps={{ danger: true }}
        cancelText="انصراف"
      >
        <Form form={addPhoneForm} layout="vertical" onFinish={submitBanPhone}>
          <Form.Item
            name="phoneNumber"
            label="شماره تلفن همراه"
            rules={[
              { required: true, message: 'شماره تلفن همراه الزامی است' },
              { pattern: /^09\d{9}$/, message: 'فرمت شماره تلفن نامعتبر است (مثال: 09123456789)' }
            ]}
          >
            <Input placeholder="مثال: 09123456789" style={{ direction: 'ltr' }} />
          </Form.Item>
          <Form.Item
            name="reason"
            label="علت مسدودسازی"
            rules={[{ required: true, message: 'علت مسدودسازی الزامی است' }]}
          >
            <Input.TextArea rows={3} placeholder="علت مسدود کردن این شماره را بنویسید..." />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
